import { TabContent, TabPane, Nav, NavItem, NavLink } from "reactstrap";
import { Suspense, useState, useRef, useCallback, useMemo, useEffect } from "react";
import Loader from "components/Loader";
import { withTranslation } from "react-i18next";
import { Portal } from "react-overlays";
import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";
import EmployeesFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/EmployeesFilter";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import StaffTrackingTable, { sortingVariables } from "./StaffTrackingTable";
import SearchBar from "accountOwner/pages/Notes/component/Filters/SearchBar";
import OfficeFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/OfficeFilter";
import StaffTrackingPolicyStatus from "./StaffTrackingPolicyStatus";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import { useStaffTrackingFilter } from "./hooks/useStaffTrackingFilter";
import { useStaffTrackingData } from "./hooks/useStaffTrackingData";
import styles from "./StaffTracking.module.scss";
import NotAccessible from "accountOwner/pages/ManagerDashboard/components/NotAccessible";
import Empty from "components/Empty";
import officePolicystyles from "../../OfficePolicyManagement.module.scss";
import { useStaffTrackingSelection } from "accountOwner/pages/OfficePolicyManagement/context/StaffTrackingSelectionContext";

export const FILTER_STORAGE_KEY_STAFF = "staffTrackingFiltersStaff";
export const FILTER_STORAGE_KEY_OFFICE = "staffTrackingFiltersOffice";
const ACTIVE_TAB_STORAGE_KEY = "staffTrackingActiveTab";
const ACCORDION_STORAGE_KEY = "staffTrackingAccordion";

const StaffTracking = ({ t, setStaffTrackingPayload }) => {
  const [activeTab, setActiveTab] = useState(() => {
    // Load from localStorage on initial render
    const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return savedTab || "1";
  });
  const [isSidebarActive, setSidebarActive] = useState(true);
  const { effectiveOwnerId, commonOwnerData, isStaff, dataAccessValues, isOwner } =
    useAccountOwnerCommon();
  const canAccessThisPage = isOwner || (isStaff && dataAccessValues.isAdmin);
  const cannotAccessThisPage = !canAccessThisPage;

  // Accordion state with sessionStorage persistence
  const [expandedAccordionItems, setExpandedAccordionItems] = useState(() => {
    // Load from sessionStorage on initial render
    try {
      const saved = sessionStorage.getItem(ACCORDION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that saved items are arrays
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      console.error("Error loading accordion state:", error);
    }
    // Default: expand first item (Issue Date)
    return ["a"];
  });

  const handleAccordionChange = (uuids) => {
    setExpandedAccordionItems(uuids);
    try {
      sessionStorage.setItem(ACCORDION_STORAGE_KEY, JSON.stringify(uuids));
    } catch (error) {
      console.error("Error saving accordion state:", error);
    }
  };

  // Use custom hook for filter management
  const filters = useStaffTrackingFilter(activeTab);
  // Use custom hook for data fetching
  const { staffCredentials } = useStaffTrackingData(
    activeTab,
    effectiveOwnerId,
    filters,
    canAccessThisPage,
    setStaffTrackingPayload,
  );

  const {
    setTrackingRows,
    setIsArchivedStaffList,
    setStaffTrackingRefetch,
    clearStaffSelection,
  } = useStaffTrackingSelection();

  useEffect(() => {
    setTrackingRows(staffCredentials?.data || []);
  }, [staffCredentials?.data, setTrackingRows]);

  useEffect(() => {
    setStaffTrackingRefetch(staffCredentials.refetch);
    return () => setStaffTrackingRefetch(null);
  }, [staffCredentials.refetch, setStaffTrackingRefetch]);

  useEffect(() => {
    setIsArchivedStaffList(activeTab === "2");
    clearStaffSelection();
  }, [activeTab, setIsArchivedStaffList, clearStaffSelection]);

  // Calendar container to render date picker outside overflow container
  const CalendarContainer = useCallback(({ children }) => {
    if (typeof document !== "undefined") {
      return <Portal container={document.body}>{children}</Portal>;
    }
    return <>{children}</>;
  }, []);

  const handleSidebarToggle = () => {
    setSidebarActive(!isSidebarActive);
  };

  const timerRef = useRef(null);
  const handleSearchTerm = (event) => {
    filters.setSearchValue(event.target.value);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      filters.setSearchValueApplied(event.target.value);
    }, 500);
  };
  const currentActiveTab = useMemo(() => {
    return { firstTab: activeTab === "1", secondTab: activeTab === "2" };
  }, [activeTab]);

  // Save activeTab to localStorage whenever it changes
  const setActiveTabToLocalStorage = useCallback((activeTab) => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
    setActiveTab(activeTab);
  }, []);

  return (
    <>
      {" "}
      {staffCredentials.isLoading && <Loader />}
      <div className="common-tabs">
        <Nav tabs>
          <NavItem>
            <NavLink
              className={currentActiveTab.firstTab ? "active" : ""}
              onClick={() => {
                setActiveTabToLocalStorage("1");
                filters.handleSetSortByAndIsDescending(
                  sortingVariables.UserName,
                  true,
                );
              }}
            >
              {t("officePolicyManagement.currentPolicies")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={currentActiveTab.secondTab ? "active" : ""}
              onClick={() => {
                setActiveTabToLocalStorage("2");
                filters.handleSetSortByAndIsDescending(
                  sortingVariables.UserName,
                  true,
                );
              }}
            >
              {t("officePolicyManagement.archivedPolicies")}
            </NavLink>
          </NavItem>
        </Nav>
        <div className={"scheduler-tabs-main-wrapper"}>
          <StickySidebar
            isSidebarActive={isSidebarActive}
            handleSidebarToggle={handleSidebarToggle}
            resetFilter={() => {}}
          >
            <div className={`${styles["filter-title-box"]}`}>
              <span>
                {t("notes.filters")}
                {!!filters.isFilterApplied && (
                  <span className={styles.red_dot}></span>
                )}
              </span>
            </div>

            <Accordion
              preExpanded={expandedAccordionItems}
              className="filter-accordion"
              allowZeroExpanded
              allowMultipleExpanded
              onChange={handleAccordionChange}
            >
              <AccordionItem uuid="a">
                <AccordionItemHeading>
                  <AccordionItemButton>
                    {t("officePolicyManagement.issueDate")}
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <div className="d-flex gap-base">
                    <div className="c-field w-100">
                      <label>{t("officePolicyManagement.from")}</label>
                      <div className="d-flex inputdate">
                        <ReactDatePickerCommon
                          dateFormat="dd-MM-yyyy"
                          className="c-form-control"
                          onChange={(e) => {
                            filters.setStartDate(e);
                          }}
                          selected={filters.startDate}
                          popperContainer={CalendarContainer}
                        />
                      </div>
                    </div>
                    <div className="c-field w-100">
                      <label>{t("officePolicyManagement.to")}</label>
                      <div className="d-flex inputdate">
                        <ReactDatePickerCommon
                          dateFormat="dd-MM-yyyy"
                          className="c-form-control"
                          onChange={(e) => {
                            filters.setEndDate(e);
                          }}
                          minDate={filters.startDate}
                          selected={filters.endDate}
                          popperContainer={CalendarContainer}
                        />
                      </div>
                    </div>
                  </div>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem
                uuid="b"
                className="accordion__item border-0 mt-0 p-0"
              >
                <AccordionItemHeading>
                  <AccordionItemButton>
                    {t("accountOwner.office")}
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <OfficeFilter
                    setOfficeFilter={filters.setSelectedOfficeFilter}
                    selectedOffice={filters.selectedOfficeFilter}
                    selectedOwnerId={effectiveOwnerId}
                    includeOwnerOffices={true}
                    allElement={() => null}
                  />
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem uuid="c" className="accordion__item m-0 p-0">
                <AccordionItemHeading>
                  <AccordionItemButton>
                    {t("accountOwner.staff")}
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel
                  className={
                    officePolicystyles["staff-filter-panel"] +
                    " accordion__panel"
                  }
                >
                  <EmployeesFilter
                    selectedEmpFilter={filters.selectedEmpFilter}
                    setEmpFilter={filters.setSelectedEmpFilter}
                    ownerId={effectiveOwnerId}
                    ownerData={commonOwnerData || {}}
                    selectedOfficeFilter={filters.selectedOfficeFilter}
                    selectedRoles={filters.selectedRoles}
                    setSelectedRoles={filters.setSelectedRoles}
                    resetSearchTrigger={filters.resetSearchTrigger}
                    hideOwner={isOwner}
                    idSelectionType="userId"
                    deduplicateByUserId={true}
                  />
                </AccordionItemPanel>
              </AccordionItem>

              <AccordionItem uuid="d">
                <AccordionItemHeading>
                  <AccordionItemButton>Status</AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <StaffTrackingPolicyStatus
                    selectedValues={filters.statuses}
                    setSelectedValues={filters.setStatuses}
                  />
                </AccordionItemPanel>
              </AccordionItem>
            </Accordion>
            <div className="filter-btn-box filter-btn-box--sticky-footer ">
              <button
                className={"button button-round button-shadow mr-3"}
                title={t("apply")}
                onClick={filters.handleApplyFilter}
              >
                {" "}
                {t("apply")}
              </button>
              <button
                className={
                  "button button-round button-border button-dark reset-btn"
                }
                onClick={filters.resetFilter}
                title={t("reset")}
              >
                {t("reset")}
              </button>
              <button
                className={
                  "button button-round button-border button-dark cancel-btn bg-transparent"
                }
                title={t("close")}
                onClick={handleSidebarToggle}
              >
                {t("close")}
              </button>
            </div>
          </StickySidebar>
          <TabContent
            activeTab={activeTab}
            className={
              cannotAccessThisPage ||
              (!staffCredentials.isLoading &&
                (!staffCredentials?.data ||
                  staffCredentials?.data?.length === 0))
                ? "w-100"
                : styles["tab-content-box"]
            }
          >
            {canAccessThisPage && (
              <div
                className={` ${styles["listing-search-box"]} ${isSidebarActive ? styles["listing-search-box-mobile"] : ""}`}
              >
                <SearchBar
                  parentClass={` ${styles["listing-search-bar"]} `}
                  searchValue={filters.searchValue}
                  handleSearchTerm={handleSearchTerm}
                  t={t}
                  placeholder={t("officePolicyManagement.searchByName")}
                />
              </div>
            )}

            <Suspense fallback={<Loader />}>
              {cannotAccessThisPage ? (
                <div
                  className="w-100 not-accessible center_property"
                  style={{ height: "300px" }}
                >
                  <NotAccessible Message={t("dashBoard.notAccessible")} />
                </div>
              ) : (
                <>
                  <TabPane tabId="1">
                    {!staffCredentials.isLoading &&
                    (!staffCredentials?.data ||
                      staffCredentials?.data?.length === 0) ? (
                      <div
                        className="w-100 center_property"
                        style={{ height: "300px" }}
                      >
                        <Empty
                          Message={t(
                            "officePolicyManagement.noDataAvailableForSelection",
                          )}
                        />
                      </div>
                    ) : (
                      activeTab === "1" && (
                        <StaffTrackingTable
                          data={staffCredentials?.data || []}
                          isLoading={staffCredentials.isLoading}
                          refetchData={staffCredentials.refetch}
                          sortBy={filters.sortBy}
                          handleSetSortByAndIsDescending={
                            filters.handleSetSortByAndIsDescending
                          }
                          isDescending={filters.isDescending}
                          isArchived={false}
                          key="current"
                          refetch={staffCredentials.refetch}
                        />
                      )
                    )}
                  </TabPane>
                  <TabPane tabId="2">
                    {!staffCredentials.isLoading &&
                    (!staffCredentials?.data ||
                      staffCredentials?.data?.length === 0) ? (
                      <div
                        className="w-100 center_property"
                        style={{ height: "300px" }}
                      >
                        <Empty
                          Message={t(
                            "officePolicyManagement.noDataAvailableForSelection",
                          )}
                        />
                      </div>
                    ) : (
                      activeTab === "2" && (
                        <StaffTrackingTable
                          data={staffCredentials?.data || []}
                          isLoading={staffCredentials.isLoading}
                          refetchData={staffCredentials.refetch}
                          handleSetSortByAndIsDescending={
                            filters.handleSetSortByAndIsDescending
                          }
                          sortBy={filters.sortBy}
                          isDescending={filters.isDescending}
                          isArchived={true}
                          key="archived"
                          refetch={staffCredentials.refetch}
                        />
                      )
                    )}
                  </TabPane>
                </>
              )}
            </Suspense>
          </TabContent>
        </div>
      </div>
    </>
  );
};
export default withTranslation()(StaffTracking);
